import { useState, useEffect, useRef, useCallback } from "react";
import * as Ably from "ably";
import { LITE } from "./lite";

// ─── Sound hook (mirrors AmericaSays pattern) ─────────────────────────────────
function useSound() {
  const correctRef     = useRef(null);
  const adminRevealRef = useRef(null);
  const gameOverRef    = useRef(null);
  const bgRef          = useRef(null);

  useEffect(() => {
    const ext  = new Audio().canPlayType('audio/ogg; codecs=vorbis') ? 'ogg' : 'mp3';
    const load = (name, opts = {}) => {
      const el = new Audio(`/sounds/${name}.${ext}`);
      el.preload = 'auto';
      Object.assign(el, opts);
      return el;
    };
    correctRef.current     = load('correct');
    adminRevealRef.current = load('admin_reveal');
    gameOverRef.current    = load('game_over');
    bgRef.current          = load('bg_music', { loop: true, volume: 0.32 });
    return () => {
      [correctRef, adminRevealRef, gameOverRef, bgRef].forEach(r => {
        if (!r.current) return;
        r.current.pause(); r.current.src = ''; r.current = null;
      });
    };
  }, []);

  const play = (ref, vol = 0.80) => {
    const el = ref.current; if (!el) return;
    el.currentTime = 0; el.volume = vol;
    el.play().catch(() => {});
  };

  // Duck background music briefly (for correct answer / reveal sounds)
  const duckMusic = (durationMs = 1800, targetRatio = 0.18) => {
    const el = bgRef.current;
    if (!el || el.paused || el.volume < 0.01) return;
    const orig     = el.volume;
    const low      = orig * targetRatio;
    const downStep = (orig - low) / (200 / 50);
    const downIv   = setInterval(() => {
      if (!bgRef.current) { clearInterval(downIv); return; }
      bgRef.current.volume = Math.max(low, bgRef.current.volume - downStep);
      if (bgRef.current.volume <= low) clearInterval(downIv);
    }, 50);
    setTimeout(() => {
      if (!bgRef.current || bgRef.current.paused) return;
      const upStep = (orig - low) / (400 / 50);
      const upIv   = setInterval(() => {
        if (!bgRef.current) { clearInterval(upIv); return; }
        bgRef.current.volume = Math.min(orig, bgRef.current.volume + upStep);
        if (bgRef.current.volume >= orig) clearInterval(upIv);
      }, 50);
    }, durationMs);
  };

  // Unlock audio on first user gesture (browser autoplay policy)
  const unlockedRef = useRef(false);
  useEffect(() => {
    const unlock = () => {
      if (unlockedRef.current) return;
      unlockedRef.current = true;
      [correctRef, adminRevealRef, gameOverRef, bgRef].forEach(r => {
        if (!r.current) return;
        const v = r.current.volume;
        r.current.volume = 0;
        r.current.play().then(() => { r.current.pause(); r.current.currentTime = 0; r.current.volume = v; }).catch(() => {});
      });
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  return {
    correct()     { duckMusic(1600, 0.18); play(correctRef, 0.90); },
    adminReveal() { duckMusic(3500, 0.12); play(adminRevealRef, 0.88); },
    gameOver() {
      const bg = bgRef.current;
      if (bg && !bg.paused) { bg.volume = 0; bg.pause(); }
      play(gameOverRef, 0.88);
    },
    startMusic() {
      const el = bgRef.current;
      if (!el || !el.paused) return;
      el.currentTime = 0; el.volume = 0;
      el.play().catch(() => {});
      const target = 0.32, step = target / (3000 / 50);
      const iv = setInterval(() => {
        if (!bgRef.current) { clearInterval(iv); return; }
        bgRef.current.volume = Math.min(target, bgRef.current.volume + step);
        if (bgRef.current.volume >= target) clearInterval(iv);
      }, 50);
    },
    get unlocked() { return unlockedRef.current; },
    forceUnlock() {
      if (unlockedRef.current) return;
      unlockedRef.current = true;
      [correctRef, adminRevealRef, gameOverRef, bgRef].forEach(r => {
        if (!r.current) return;
        const v = r.current.volume;
        r.current.volume = 0;
        r.current.play().then(() => { r.current.pause(); r.current.currentTime = 0; r.current.volume = v; }).catch(() => {});
      });
    },
  };
}

// ─── Hooks ────────────────────────────────────────────────────────────────────
function useWindowSize() {
  const [sz, setSz] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const upd = () => setSz({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', upd);
    return () => window.removeEventListener('resize', upd);
  }, []);
  return sz;
}

// Single shared Ably client (token auth via authUrl — no root key in client)
const ABLY_AUTH_URL = (process.env.REACT_APP_API_URL || '/api') + '/ably/token';
let _ablyClient = null;
function getAblyClient() {
  if (!_ablyClient) {
    _ablyClient = new Ably.Realtime({ authUrl: ABLY_AUTH_URL, autoConnect: true });
  }
  return _ablyClient;
}

function useAblyChannel(channelName, event, handler) {
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; });
  useEffect(() => {
    if (!channelName) return;
    const client  = getAblyClient();
    if (!client) return;
    const channel = client.channels.get(channelName);
    const cb = (msg) => handlerRef.current(msg);
    channel.subscribe(event, cb);
    return () => { channel.unsubscribe(event, cb); };
  }, [channelName, event]);
}

function useCellAnimations() {
  const [anims, setAnims] = useState({});
  const trigger = useCallback((key, data) => {
    setAnims(prev => ({ ...prev, [key]: { ...data, ts: Date.now() } }));
    setTimeout(() => setAnims(prev => { const n = { ...prev }; delete n[key]; return n; }), 3200);
  }, []);
  return [anims, trigger];
}

// ─── Timer hooks ──────────────────────────────────────────────────────────────
function useElapsedTimer(startedAt, status) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAt || status !== 'active') return;
    const tick = () => setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [startedAt, status]);
  return elapsed;
}

// Countdown bar — returns pct (100→0) and secsLeft.
// Uses a stable ref for onExpired to avoid dep-array infinite loops.
function useCountdownBar(timerDuration, startedAt, status) {
  const [pct,      setPct]      = useState(100);
  const [secsLeft, setSecsLeft] = useState(timerDuration || 0);
  const [expired,  setExpired]  = useState(false);
  const rafRef  = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!timerDuration || !startedAt || status !== 'active') {
      cancelAnimationFrame(rafRef.current);
      return;
    }
    firedRef.current = false;
    setExpired(false);
    setPct(100);
    setSecsLeft(timerDuration);

    const tick = () => {
      const elapsed   = (Date.now() - startedAt) / 1000;
      const remaining = Math.max(0, timerDuration - elapsed);
      const p         = (remaining / timerDuration) * 100;
      setPct(p);
      setSecsLeft(Math.ceil(remaining));
      if (remaining <= 0) {
        setExpired(true);
        firedRef.current = true;
        return; // stop rAF
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [timerDuration, startedAt, status]);

  return { pct, secsLeft, expired };
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

// ─── Utilities ────────────────────────────────────────────────────────────────
function enrichPuzzle(puz) {
  if (!puz) return null;
  const clueNumbers = {}, whiteCells = new Set();
  (puz.clues || []).forEach(c => {
    (c.cells || []).forEach(({ row, col }) => whiteCells.add(`${row}_${col}`));
    const k = `${c.startRow}_${c.startCol}`;
    if (!clueNumbers[k]) clueNumbers[k] = c.number;
  });
  return { ...puz, clueNumbers, whiteCells };
}

function computeCellSize(puzzle, containerW, containerH) {
  if (!puzzle?.width || !puzzle?.height) return 48;
  const GAP = 3, PAD = 24;
  const fromW = Math.floor((containerW - PAD * 2 - GAP * (puzzle.width  - 1)) / puzzle.width);
  const fromH = Math.floor((containerH - PAD * 2 - GAP * (puzzle.height - 1)) / puzzle.height);
  // Ceiling raised (was 140) so the grid keeps filling large 4K/55" displays
  // instead of freezing small; still bounded by available width/height.
  return Math.max(28, Math.min(fromW, fromH, 220));
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
const STYLES = `
  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; overflow: hidden; touch-action: none; -webkit-tap-highlight-color: transparent; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(0,242,255,.2); border-radius: 3px; }
  @keyframes ambientOrb1 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(50px,-40px) scale(1.15)} }
  @keyframes ambientOrb2 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-45px,35px) scale(0.88)} }
  @keyframes ambientOrb3 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(25px,55px) scale(1.1)} }
  @keyframes scanMove    { from{transform:translateY(-100vh)} to{transform:translateY(200vh)} }
  @keyframes pulseRing   { 0%,100%{opacity:.5;transform:translate(-50%,-50%) scale(1)} 50%{opacity:.1;transform:translate(-50%,-50%) scale(1.07)} }
  @keyframes cellStamp   { 0%{transform:scale(.3);filter:brightness(4) blur(3px)} 55%{transform:scale(1.5);filter:brightness(2.2)} 100%{transform:scale(1.03);filter:brightness(1)} }
  @keyframes rippleOut   { 0%{transform:scale(.3);opacity:1} 100%{transform:scale(5.5);opacity:0} }
  @keyframes slideInRight{ from{transform:translateX(28px);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes slideInLeft { from{transform:translateX(-28px);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes slideInUp   { from{transform:translateY(32px);opacity:0} to{transform:translateY(0);opacity:1} }
  @keyframes badgeReveal { from{transform:scale(0) rotate(-18deg);opacity:0} to{transform:scale(1) rotate(0);opacity:1} }
  @keyframes fadeIn      { from{opacity:0} to{opacity:1} }
  @keyframes winnerReveal{ from{transform:scale(.3) translateY(70px);opacity:0} to{transform:scale(1) translateY(0);opacity:1} }
  @keyframes champPulse  { 0%,100%{text-shadow:0 0 80px rgba(255,215,0,1),0 0 180px rgba(255,215,0,.7)} 50%{text-shadow:0 0 130px rgba(255,215,0,1),0 0 260px rgba(255,215,0,.9),0 0 60px #fffc} }
  @keyframes headerGlow  { 0%,100%{text-shadow:0 0 30px rgba(0,242,255,.4)} 50%{text-shadow:0 0 70px rgba(0,242,255,1),0 0 140px rgba(0,242,255,.4),0 0 4px #fff} }
  @keyframes flashAnim   { 0%{opacity:0;transform:translateX(-50%) scale(.8)} 12%{opacity:1;transform:translateX(-50%) scale(1.06)} 80%{opacity:1;transform:translateX(-50%) scale(1)} 100%{opacity:0;transform:translateX(-50%) translateY(-14px)} }
  @keyframes revealFlash { 0%{opacity:0;transform:translateX(-50%) scale(.8)} 15%{opacity:1;transform:translateX(-50%) scale(1.08)} 75%{opacity:1;transform:translateX(-50%) scale(1)} 100%{opacity:0;transform:translateX(-50%) translateY(-20px)} }
  @keyframes gridBreath  { 0%,100%{filter:drop-shadow(0 28px 60px rgba(0,0,0,.97)) drop-shadow(0 0 90px rgba(0,242,255,.06))} 50%{filter:drop-shadow(0 36px 80px rgba(0,0,0,.99)) drop-shadow(0 0 160px rgba(0,242,255,.14))} }
  @keyframes liveFlash   { 0%,100%{opacity:1} 50%{opacity:.1} }
  @keyframes cornerSpin  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes clueIn      { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:translateX(0)} }
  @keyframes timerPulse  { 0%,100%{opacity:1} 50%{opacity:.4} }
  @keyframes gameOverIn  { 0%{opacity:0;transform:scale(.92)} 100%{opacity:1;transform:scale(1)} }
`;

// ─── Fireworks ────────────────────────────────────────────────────────────────
function FireworksCanvas({ active }) {
  const cvs = useRef(null), raf = useRef(null), pts = useRef([]);
  useEffect(() => {
    if (!active) { cancelAnimationFrame(raf.current); return; }
    const c = cvs.current; if (!c) return;
    const ctx = c.getContext('2d');
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    resize(); window.addEventListener('resize', resize);
    const COLS = ['#00f2ff','#ff0099','#ffd700','#00ff88','#ff6600','#cc44ff','#fff','#00ffcc'];
    const launch = () => {
      const x = 100 + Math.random() * (c.width - 200);
      const y = 80 + Math.random() * (c.height * .45);
      const col = COLS[Math.floor(Math.random() * COLS.length)];
      for (let i = 0; i < 100; i++) {
        const a = (Math.PI*2*i/100)+Math.random()*.35, s = 3+Math.random()*9;
        pts.current.push({ x,y, vx:Math.cos(a)*s, vy:Math.sin(a)*s, col, alpha:1, r:1.8+Math.random()*3, grav:.062+Math.random()*.07, drag:.984, trail:[] });
      }
    };
    let n=0; const iv = setInterval(()=>{ launch(); if(++n>=20) clearInterval(iv); }, 300); launch(); launch();
    const loop = () => {
      ctx.fillStyle='rgba(2,3,10,.17)'; ctx.fillRect(0,0,c.width,c.height);
      pts.current = pts.current.filter(p=>p.alpha>.03);
      pts.current.forEach(p => {
        p.trail.push({x:p.x,y:p.y}); if(p.trail.length>8) p.trail.shift();
        p.trail.forEach((t,ti) => { const ta=p.alpha*(ti/p.trail.length)*.5; ctx.beginPath(); ctx.arc(t.x,t.y,p.r*.55*(ti/p.trail.length),0,Math.PI*2); ctx.fillStyle=p.col+Math.floor(ta*255).toString(16).padStart(2,'0'); ctx.fill(); });
        ctx.shadowBlur=14; ctx.shadowColor=p.col;
        ctx.beginPath(); ctx.arc(p.x,p.y,p.r,0,Math.PI*2); ctx.fillStyle=p.col+Math.floor(p.alpha*255).toString(16).padStart(2,'0'); ctx.fill();
        ctx.beginPath(); ctx.arc(p.x,p.y,p.r*.35,0,Math.PI*2); ctx.fillStyle='#fff'+Math.floor(p.alpha*170).toString(16).padStart(2,'0'); ctx.fill();
        ctx.shadowBlur=0;
        p.x+=p.vx; p.y+=p.vy; p.vx*=p.drag; p.vy*=p.drag; p.vy+=p.grav; p.alpha-=.011;
      });
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf.current); clearInterval(iv); window.removeEventListener('resize', resize); pts.current=[]; };
  }, [active]);
  if (!active) return null;
  return <canvas ref={cvs} style={{ position:'fixed',inset:0,zIndex:998,pointerEvents:'none' }} />;
}

// ─── Animated Score ───────────────────────────────────────────────────────────
function AnimatedScore({ value }) {
  const [disp, setDisp] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current === value) return;
    const from = prev.current; prev.current = value; let i=0;
    const iv = setInterval(()=>{ i++; setDisp(Math.round(from+(value-from)*i/25)); if(i>=25) clearInterval(iv); },28);
    return () => clearInterval(iv);
  }, [value]);
  return <>{disp}</>;
}

// ─── Crossword Cell ───────────────────────────────────────────────────────────
function CrosswordCell({ row, col, puzzle, filledCells, anims, cs }) {
  const key     = `${row}_${col}`;
  const isBlack = !puzzle?.whiteCells?.has(key);
  const clueNum = puzzle?.clueNumbers?.[key];
  const anim    = anims[key];
  const BR = Math.max(3, Math.round(cs*.08));
  const BW = Math.max(1, Math.round(cs*.026));

  const filled = Object.values(filledCells).find(f => {
    if (!f?.answer || f.startRow == null) return false;
    return f.direction === 'across'
      ? f.startRow === row && col >= f.startCol && col < f.startCol + f.answer.length
      : f.startCol === col && row >= f.startRow && row < f.startRow + f.answer.length;
  }) || null;

  const letter = filled ? (filled.direction==='across' ? filled.answer[col-filled.startCol] : filled.answer[row-filled.startRow]) : '';
  const tc     = filled?.teamColor || '#00f2ff';
  const numSz  = Math.max(7, Math.round(cs*.21));
  const letSz  = Math.max(12, Math.round(cs*.5));

  if (isBlack) return (
    <div style={{ width:cs, height:cs, borderRadius:BR, background:'linear-gradient(145deg,#050810,#020408)', boxShadow:'inset 0 2px 4px rgba(0,0,0,.98)' }} />
  );

  return (
    <div style={{
      width:cs, height:cs, position:'relative', borderRadius:BR,
      background: filled ? `linear-gradient(145deg,${tc}2a,${tc}0e)` : 'linear-gradient(145deg,#0d1e40,#071228)',
      border:`${BW}px solid ${filled ? tc+'88' : '#1a3458'}`,
      display:'flex', alignItems:'center', justifyContent:'center',
      transition:'all .3s cubic-bezier(.34,1.56,.64,1)',
      transform: anim ? 'scale(1.42)' : filled ? 'scale(1.02)' : 'scale(1)',
      boxShadow: filled
        ? `0 0 ${cs*.3}px ${tc}55,0 0 ${cs*.65}px ${tc}22,inset 0 1px 0 rgba(255,255,255,.18),0 ${cs*.1}px ${cs*.22}px rgba(0,0,0,.8)`
        : `inset 0 1px 0 rgba(255,255,255,.06),inset 0 -1px 0 rgba(0,0,0,.6),0 ${cs*.07}px ${cs*.14}px rgba(0,0,0,.7)`,
      animation: anim ? 'cellStamp .65s cubic-bezier(.34,1.56,.64,1)' : 'none',
      zIndex: anim ? 20 : filled ? 3 : 1, overflow:'visible',
    }}>
      <div style={{ position:'absolute', top:0, left:'8%', right:'8%', height:BW+1, borderRadius:'0 0 2px 2px',
        background: filled ? `linear-gradient(90deg,transparent,${tc}99,transparent)` : 'linear-gradient(90deg,transparent,rgba(255,255,255,.12),transparent)',
        pointerEvents:'none' }} />
      {clueNum && !anim && (
        <span style={{ position:'absolute', top:Math.round(cs*.055), left:Math.round(cs*.07),
          fontSize:numSz, fontWeight:900, color:filled?'#ffffffcc':'#ffffff55',
          fontFamily:"'Orbitron',monospace", lineHeight:1, pointerEvents:'none',
          textShadow:filled?`0 0 6px ${tc}`:'none' }}>{clueNum}</span>
      )}
      {letter && (
        <span style={{ fontSize:letSz, fontWeight:900, color:'#ffffff', fontFamily:"'Orbitron',monospace",
          textShadow:`0 0 ${cs*.14}px ${tc}cc,0 0 ${cs*.3}px ${tc}66,0 2px 4px rgba(0,0,0,.95)`,
          lineHeight:1, zIndex:2, userSelect:'none' }}>{letter}</span>
      )}
      {anim?.initials && (
        <div style={{ position:'absolute', bottom:Math.round(cs*.04), right:Math.round(cs*.04),
          background:`linear-gradient(135deg,${anim.teamColor||'#00f2ff'},${anim.teamColor||'#00f2ff'}cc)`,
          color:'#000', fontSize:Math.max(6,Math.round(cs*.13)), fontWeight:900,
          padding:`1px ${Math.round(cs*.07)}px`, borderRadius:3, fontFamily:"'Orbitron',monospace",
          animation:'badgeReveal .4s ease', zIndex:3,
          boxShadow:`0 0 12px ${anim.teamColor||'#00f2ff'}99` }}>{anim.initials}</div>
      )}
      {anim && [0,1].map(i => (
        <div key={i} style={{ position:'absolute', inset:-(14+i*18), borderRadius:'50%',
          border:`2px solid ${anim.teamColor||'#00f2ff'}${i===0?'':'44'}`,
          animation:`rippleOut 1s ease ${i*.2}s forwards`, pointerEvents:'none' }} />
      ))}
    </div>
  );
}

// ─── Clue List Panel ──────────────────────────────────────────────────────────
// font sizes bumped for legibility
function ClueListPanel({ title, clues, filledCells, compact }) {
  const filledKeys = new Set(Object.keys(filledCells));
  const isFilled   = c => filledKeys.has(`${c.startRow}_${c.startCol}_${c.direction}`);
  const open       = clues.filter(c => !isFilled(c));
  const done       = clues.filter(c =>  isFilled(c));

  const renderClue = (c, idx) => {
    const solved = isFilled(c);
    return (
      <div key={`${c.number}-${c.direction}`} style={{
        display:'flex', gap: compact ? 9 : 11,
        padding: compact ? 'clamp(5px,.7vh,8px) 0' : 'clamp(7px,1vh,11px) 0',
        borderBottom:'1px solid rgba(0,242,255,.05)',
        opacity: solved ? 0.25 : 1,
        transition:'opacity .5s ease',
        animation: !solved ? `clueIn .3s ease ${Math.min(idx, 8)*.04}s both` : 'none',
      }}>
        {/* clue number */}
        <span style={{
          fontSize: compact ? 'clamp(11px,1.2vw,20px)' : 'clamp(13px,1.5vw,24px)',
          fontWeight:900, color:'#00f2ff',
          fontFamily:"'Orbitron',monospace",
          minWidth: compact ? 22 : 26, flexShrink:0, lineHeight:1.6,
        }}>{c.number}</span>
        {/* clue text */}
        <span style={{
          fontSize: compact ? 'clamp(12px,1.3vw,21px)' : 'clamp(14px,1.6vw,25px)',
          lineHeight:1.55,
          color: solved ? '#1a2d40' : '#d8eeff',
          fontFamily:"'Share Tech Mono',monospace",
          textDecoration: solved ? 'line-through' : 'none',
          flex:1, wordBreak:'break-word',
        }}>{c.clue}</span>
        {solved && <span style={{ color:'#00ff88', fontSize:'clamp(12px,1.3vw,21px)', flexShrink:0, lineHeight:1.6 }}>✓</span>}
      </div>
    );
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' }}>
      <div style={{
        fontSize: compact ? 'clamp(9px,1vw,16px)' : 'clamp(10px,1.1vw,19px)', fontWeight:700, letterSpacing:4,
        color:'rgba(0,242,255,.7)', fontFamily:"'Orbitron',monospace",
        padding: compact ? 'clamp(8px,1.2vh,12px) clamp(10px,1.4vw,16px) clamp(5px,.8vh,9px)' : 'clamp(10px,1.5vh,16px) clamp(14px,1.8vw,22px) clamp(7px,1vh,11px)',
        borderBottom:'1px solid rgba(0,242,255,.1)', flexShrink:0,
        display:'flex', alignItems:'center', justifyContent:'space-between',
      }}>
        <span>{title}</span>
        <span style={{ fontSize:'clamp(9px,1vw,11px)', color:'rgba(0,242,255,.4)', fontFamily:"'Orbitron',monospace" }}>
          {done.length}/{clues.length}
        </span>
      </div>
      <div style={{ flex:1, overflowY:'auto', overflowX:'hidden', padding: compact ? 'clamp(4px,.6vh,6px) clamp(10px,1.4vw,16px) clamp(8px,1.2vh,12px)' : 'clamp(5px,.8vh,8px) clamp(14px,1.8vw,22px) clamp(10px,1.5vh,14px)' }}>
        {open.map((c, i) => renderClue(c, i))}
        {done.map((c, i) => renderClue(c, open.length + i))}
      </div>
    </div>
  );
}

// ─── Countdown Timer Bar (bottom of grid) ─────────────────────────────────────
// revealed=true: admin revealed all answers, bar freezes + shows REVEALED
function CountdownBar({ timerDuration, startedAt, status, revealed }) {
  const { pct, secsLeft, expired } = useCountdownBar(timerDuration, startedAt, status);

  // Show bar if: active with timer, OR revealed mid-game, OR just expired
  const hasTimer = timerDuration && startedAt;
  if (!hasTimer && !revealed) return null;
  if (status !== 'active' && !revealed && !expired) return null;

  const barColor  = revealed ? '#ff9900'
    : expired    ? '#ff4b4b'
    : pct > 55   ? '#00f2ff'
    : pct > 25   ? '#ffd700'
    : '#ff4b4b';
  const glowColor = revealed ? 'rgba(255,153,0,.6)'
    : expired   ? 'rgba(255,75,75,.7)'
    : pct > 55  ? 'rgba(0,242,255,.6)'
    : pct > 25  ? 'rgba(255,215,0,.6)'
    : 'rgba(255,75,75,.7)';

  const displayPct  = revealed ? 0 : pct;
  const labelText   = revealed ? '👁 REVEALED'
    : expired       ? "TIME'S UP"
    : formatTime(secsLeft);
  const shouldPulse = (expired || secsLeft <= 5) && !revealed;

  return (
    <div style={{
      position:'absolute', bottom:0, left:0, right:0, zIndex:50,
      background:'rgba(2,5,18,.95)', borderTop:`1px solid ${barColor}33`,
      padding:'clamp(6px,.9vh,10px) clamp(14px,2vw,24px) clamp(8px,1.1vh,12px)', backdropFilter:'blur(16px)',
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:'clamp(10px,1.4vw,18px)' }}>
        <div style={{ flex:1, height:'clamp(6px,.9vh,12px)', borderRadius:99, background:'rgba(255,255,255,.08)', overflow:'hidden' }}>
          <div style={{
            height:'100%', borderRadius:99,
            width:`${displayPct}%`,
            background:`linear-gradient(90deg,${barColor}99,${barColor})`,
            boxShadow:`0 0 14px ${glowColor}`,
            transition: revealed ? 'width .6s ease' : 'background .4s ease',
          }} />
        </div>
        <div style={{
          minWidth:'clamp(60px,8vw,100px)', textAlign:'center', fontSize:'clamp(11px,1.4vw,17px)', fontWeight:700,
          fontFamily:"'Orbitron',monospace", letterSpacing:1,
          color: barColor,
          textShadow:`0 0 12px ${barColor}`,
          animation: shouldPulse ? 'timerPulse .5s ease infinite' : 'none',
          whiteSpace:'nowrap',
        }}>
          {labelText}
        </div>
      </div>
    </div>
  );
}

// ─── Elapsed Timer Widget (right panel) ──────────────────────────────────────
function TimerWidget({ elapsed, status, compact }) {
  const isActive = status === 'active';
  return (
    <div style={{
      background: isActive ? 'linear-gradient(135deg,rgba(0,242,255,.1),rgba(0,242,255,.04))' : 'rgba(255,255,255,.02)',
      border:`1px solid ${isActive ? 'rgba(0,242,255,.3)' : 'rgba(255,255,255,.06)'}`,
      borderRadius: compact ? 10 : 14, padding: compact ? '8px 14px' : '10px 18px', textAlign:'center',
      boxShadow: isActive ? '0 0 28px rgba(0,242,255,.12),inset 0 1px 0 rgba(0,242,255,.1)' : 'none',
    }}>
      <div style={{ fontSize: compact?8:9, fontWeight:700, letterSpacing:4,
        color:'rgba(0,242,255,.5)', fontFamily:"'Orbitron',monospace",
        marginBottom:compact?3:4, textTransform:'uppercase' }}>
        {isActive ? '⏱ Elapsed' : status==='completed' ? '✓ Done' : '⏸ Waiting'}
      </div>
      <div style={{ fontSize:compact?'clamp(24px,2.4vw,40px)':'clamp(32px,3vw,56px)', fontWeight:900, fontFamily:"'Orbitron',monospace",
        letterSpacing:compact?1:2, color:isActive?'#00f2ff':'rgba(0,242,255,.3)',
        textShadow:isActive?'0 0 24px rgba(0,242,255,.8)':'none', lineHeight:1 }}>
        {formatTime(elapsed)}
      </div>
    </div>
  );
}

// ─── Mini Team Scores ─────────────────────────────────────────────────────────
function MiniTeamScores({ teamScores }) {
  if (!teamScores?.length) return null;
  const medals = ['#ffd700','#d0d0d0','#cd7f32'];
  return (
    <div>
      <div style={{ fontSize:9, fontWeight:700, letterSpacing:4,
        color:'rgba(255,215,0,.55)', fontFamily:"'Orbitron',monospace",
        padding:'8px 14px 5px', borderBottom:'1px solid rgba(255,255,255,.06)',
        display:'flex', alignItems:'center', gap:6 }}>
        <span>🏆</span><span>Teams</span>
      </div>
      <div style={{ padding:'4px 14px 6px' }}>
        {teamScores.map((t,i) => (
          <div key={t.id} style={{ display:'flex', alignItems:'center', gap:7,
            padding:'5px 0', borderBottom:i<teamScores.length-1?'1px solid rgba(255,255,255,.04)':'none' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:t.color, flexShrink:0 }} />
            <div style={{ flex:1, fontSize:'clamp(11px,1.2vw,22px)', fontWeight:700, fontFamily:"'Exo 2',sans-serif",
              color:i===0?'#ffd700':i===1?'#d0d0d0':i===2?'#cd7f32':'#b0ccee',
              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.name}</div>
            <div style={{ fontSize:'clamp(12px,1.3vw,24px)', fontWeight:900, fontFamily:"'Orbitron',monospace",
              color:i===0?'#ffd700':(t.color||'#00f2ff') }}>{t.score}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Mini Leaderboard ─────────────────────────────────────────────────────────
function MiniLeaderboard({ scores, compact }) {
  const top    = [...(scores||[])].sort((a,b)=>b.score-a.score).slice(0,6);
  const metals = ['#ffd700','#d0d0d0','#cd7f32'];
  return (
    <div>
      <div style={{ fontSize:'clamp(9px,1vw,18px)', fontWeight:700, letterSpacing:4,
        color:'rgba(255,215,0,.7)', fontFamily:"'Orbitron',monospace",
        padding:'clamp(9px,1.2vh,14px) clamp(12px,1.5vw,20px) clamp(5px,.8vh,9px)',
        borderBottom:'1px solid rgba(255,255,255,.06)',
        display:'flex', alignItems:'center', gap:6 }}>
        <span>🏆</span><span>Rankings</span>
      </div>
      <div style={{ padding:'clamp(4px,.6vh,7px) clamp(12px,1.5vw,20px) clamp(8px,1.2vh,12px)' }}>
        {top.length===0
          ? <div style={{ color:'rgba(0,242,255,.15)', fontSize:'clamp(10px,1.1vw,13px)', textAlign:'center', padding:'10px 0', fontFamily:"'Orbitron',monospace", letterSpacing:3 }}>AWAITING PLAYERS</div>
          : top.map((s,i) => (
            <div key={s.jid||i} style={{ display:'flex', alignItems:'center', gap:'clamp(6px,.8vw,10px)',
              padding:'clamp(6px,.9vh,10px) 0', opacity:s.left?.4:1,
              borderBottom:i<top.length-1?'1px solid rgba(255,255,255,.04)':'none' }}>
              <div style={{ width:'clamp(22px,2.6vw,46px)', height:'clamp(22px,2.6vw,46px)', borderRadius:'clamp(5px,.6vw,10px)', flexShrink:0,
                display:'flex', alignItems:'center', justifyContent:'center',
                background:i<3?`${metals[i]}1e`:'rgba(255,255,255,.03)',
                border:`1px solid ${i<3?metals[i]+'44':'rgba(255,255,255,.05)'}`,
                fontSize:'clamp(10px,1.1vw,20px)', fontWeight:900, color:i<3?metals[i]:'#1e3050',
                fontFamily:"'Orbitron',monospace" }}>{i+1}</div>
              <div style={{ width:'clamp(3px,.35vw,6px)', height:'clamp(22px,2.6vw,46px)', borderRadius:2, flexShrink:0,
                background:s.teamColor||(i<3?metals[i]:'#1a2d4a') }} />
              <div style={{ flex:1, minWidth:0, fontSize:'clamp(13px,1.5vw,26px)', fontWeight:i===0?800:600,
                fontFamily:"'Exo 2',sans-serif",
                color:i===0?'#ffd700':i===1?'#d0d0d0':i===2?'#cd7f32':'#b0ccee',
                whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{s.name}</div>
              <div style={{ fontSize:'clamp(15px,1.9vw,34px)', fontWeight:900, fontFamily:"'Orbitron',monospace",
                color:i===0?'#ffd700':(s.teamColor||'#00f2ff'),
                textShadow:i===0?'0 0 16px rgba(255,215,0,.7)':'none' }}>
                <AnimatedScore value={s.score} /></div>
            </div>
          ))}
      </div>
    </div>
  );
}

// ─── Progress Bar ─────────────────────────────────────────────────────────────
function ProgressBar({ puzzle, filledCells }) {
  const total  = (puzzle?.clues||[]).length;
  const solved = Object.keys(filledCells).length;
  const pct    = total > 0 ? Math.min(100, (solved/total)*100) : 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
      <div style={{ flex:1, height:'clamp(4px,.6vh,8px)', background:'rgba(0,242,255,.08)', borderRadius:3 }}>
        <div style={{ height:'100%', borderRadius:3, width:`${pct}%`,
          background:'linear-gradient(90deg,#00f2ff,#00ff88)',
          boxShadow:'0 0 10px rgba(0,242,255,.7)',
          transition:'width .8s cubic-bezier(.34,1.56,.64,1)' }} />
      </div>
      <span style={{ fontSize:'clamp(10px,1.1vw,14px)', color:'rgba(0,242,255,.6)', fontFamily:"'Orbitron',monospace",
        fontWeight:700, flexShrink:0 }}>{solved}/{total}</span>
    </div>
  );
}

// ─── GAME OVER OVERLAY ────────────────────────────────────────────────────────
function GameOverOverlay({ scores, teamScores, active, onDismiss }) {
  const sorted = [...(scores||[])].sort((a,b)=>b.score-a.score);
  const winner = sorted[0];
  if (!active || !winner) return null;
  const metals = ['#ffd700','#d0d0d0','#cd7f32'];
  const hasTeams = teamScores?.length > 0;
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:997,
      background:'rgba(2,3,10,.97)',
      display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column',
      backdropFilter:'blur(28px)', animation:'gameOverIn .8s cubic-bezier(.34,1.56,.64,1)',
    }}
    onClick={onDismiss}
    >
      {/* Pulsing rings */}
      {[...Array(6)].map((_,i) => (
        <div key={i} style={{
          position:'absolute', borderRadius:'50%',
          width:120+i*110, height:120+i*110,
          border:`1px solid rgba(255,215,0,${.18-i*.025})`,
          top:'50%', left:'50%', transform:'translate(-50%,-50%)',
          animation:`pulseRing ${3.5+i*.5}s ease infinite ${i*.35}s`,
          pointerEvents:'none',
        }} />
      ))}

      <div style={{ textAlign:'center', position:'relative', zIndex:1, animation:'winnerReveal 1s cubic-bezier(.34,1.56,.64,1) .3s both',
        maxWidth:'90vw', overflow:'auto', maxHeight:'95vh', padding:'0 20px' }}>
        {/* Game over badge */}
        <div style={{ fontSize:11, letterSpacing:8, color:'rgba(255,215,0,.55)',
          fontFamily:"'Orbitron',monospace", fontWeight:700, marginBottom:20,
          animation:'fadeIn .6s ease .8s both' }}>
          ◈  PUZZLE COMPLETE  ·  FINAL SCORES  ◈
        </div>

        {/* Team standings (when team mode) */}
        {hasTeams && (
          <div style={{ marginBottom:32, animation:'fadeIn .6s ease .5s both' }}>
            <div style={{ fontSize:10, letterSpacing:5, color:'rgba(255,255,255,.3)',
              fontFamily:"'Orbitron',monospace", marginBottom:14 }}>TEAM STANDINGS</div>
            <div style={{ display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap' }}>
              {teamScores.map((t,i) => (
                <div key={t.id} style={{
                  background:`${t.color}14`,
                  border:`2px solid ${t.color}${i===0?'88':'44'}`,
                  borderRadius:14, padding:'14px 24px', textAlign:'center',
                  animation:`slideInUp .5s ease ${.6+i*.12}s both`,
                  minWidth:120,
                }}>
                  <div style={{ fontSize:9, fontFamily:"'Orbitron',monospace", letterSpacing:4,
                    color:metals[i]||'rgba(255,255,255,.3)', marginBottom:6 }}>
                    {i===0?'🥇 1ST':i===1?'🥈 2ND':i===2?'🥉 3RD':`#${i+1}`}
                  </div>
                  <div style={{ width:12, height:12, borderRadius:'50%', background:t.color,
                    margin:'0 auto 6px', boxShadow:`0 0 12px ${t.color}` }} />
                  <div style={{ fontSize:18, fontWeight:800, fontFamily:"'Exo 2',sans-serif",
                    color:i===0?'#ffd700':'#fff', marginBottom:4 }}>{t.name}</div>
                  <div style={{ fontSize:22, fontWeight:900, fontFamily:"'Orbitron',monospace",
                    color:t.color, textShadow:`0 0 20px ${t.color}` }}>{t.score}</div>
                  <div style={{ fontSize:9, color:'rgba(255,255,255,.3)',
                    fontFamily:"'Orbitron',monospace", marginTop:3 }}>{t.playerCount} player{t.playerCount!==1?'s':''}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Individual winner */}
        <div style={{ fontSize:hasTeams?10:11, letterSpacing:6, color:'rgba(255,215,0,.45)',
          fontFamily:"'Orbitron',monospace", fontWeight:700, marginBottom:hasTeams?8:0,
          animation:'fadeIn .6s ease .8s both' }}>
          {hasTeams ? 'TOP PLAYER' : ''}
        </div>

        {/* Winner name */}
        <div style={{
          fontSize:Math.min(hasTeams?72:100, Math.max(hasTeams?32:44, (hasTeams?520:680)/Math.max(winner.name.length,1))),
          fontWeight:900, fontFamily:"'Orbitron',monospace",
          color:'#ffd700', lineHeight:1, marginBottom:12,
          textShadow:'0 0 80px rgba(255,215,0,1),0 0 180px rgba(255,215,0,.7),0 0 40px #fff4',
          animation:'champPulse 2s ease infinite',
        }}>{winner.name}</div>

        {/* Winner score */}
        <div style={{ fontSize:hasTeams?44:60, fontWeight:900, fontFamily:"'Orbitron',monospace",
          color:'#00f2ff', marginBottom:hasTeams?24:48, letterSpacing:-2,
          textShadow:'0 0 60px rgba(0,242,255,.95)',
          animation:'fadeIn .5s ease .7s both' }}>
          <AnimatedScore value={winner.score} /> PTS
        </div>

        {/* Runner-up cards */}
        <div style={{ display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap',
          animation:'fadeIn .6s ease 1.1s both' }}>
          {sorted.slice(1,4).map((s,i) => (
            <div key={i} style={{
              background:'rgba(255,255,255,.04)',
              border:`1px solid ${metals[i+1]||'rgba(255,255,255,.08)'}44`,
              borderRadius:16, padding:'18px 28px', textAlign:'center',
              animation:`slideInUp .5s ease ${1.3+i*.14}s both`,
            }}>
              <div style={{ color:metals[i+1]||'#2a3a50', fontSize:10,
                fontFamily:"'Orbitron',monospace", letterSpacing:4, marginBottom:7 }}>
                {['2ND','3RD','4TH'][i]}
              </div>
              <div style={{ color:'#fff', fontSize:19, fontWeight:700,
                fontFamily:"'Exo 2',sans-serif", marginBottom:5 }}>{s.name}</div>
              <div style={{ color:'#3a5080', fontSize:17, fontFamily:"'Orbitron',monospace" }}>{s.score}</div>
            </div>
          ))}
        </div>

        {/* Dismiss hint */}
        <div style={{ marginTop:36, fontSize:10, color:'rgba(255,255,255,.2)',
          fontFamily:"'Orbitron',monospace", letterSpacing:4,
          animation:'fadeIn .5s ease 2s both' }}>
          TAP ANYWHERE TO DISMISS
        </div>
      </div>
    </div>
  );
}

// ─── QR Code ──────────────────────────────────────────────────────────────────
function QRCode({ url, size=200 }) {
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}&color=00f2ff&bgcolor=020612&format=png&qzone=1`;
  return (
    <div style={{ padding:14, background:'rgba(0,242,255,.05)', border:'2px solid rgba(0,242,255,.28)',
      borderRadius:18, boxShadow:'0 0 60px rgba(0,242,255,.15)' }}>
      <img src={src} alt="QR" width={size} height={size} style={{ display:'block', borderRadius:8 }} />
    </div>
  );
}

// ─── Nav Bar ──────────────────────────────────────────────────────────────────
function NavBar({ gameId, view, onViewChange, puzzle, status, elapsed }) {
  const [copied, setCopied] = useState(false);
  const views = [{ id:'display', label:'GRID' }, { id:'scores', label:'SCORES' }, { id:'join', label:'JOIN' }];
  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}?game=${gameId}&view=${view}`);
    setCopied(true); setTimeout(()=>setCopied(false),2000);
  };

  return (
    <div style={{ position:'relative', zIndex:100, height:'clamp(48px,7vh,68px)',
      padding:'0 clamp(10px,1.2vw,18px)', display:'flex', alignItems:'center', gap:'clamp(6px,.8vw,12px)',
      background:'linear-gradient(180deg,rgba(2,4,16,.99) 0%,rgba(2,5,14,.97) 100%)',
      borderBottom:'1px solid rgba(0,242,255,.12)',
      boxShadow:'0 4px 48px rgba(0,0,0,.9)', flexShrink:0 }}>

      {/* Logo */}
      <div style={{ width:'clamp(28px,3.5vw,42px)', height:'clamp(28px,3.5vw,42px)', borderRadius:'clamp(6px,.8vw,10px)', flexShrink:0, position:'relative',
        background:'rgba(0,242,255,.08)', border:'1px solid rgba(0,242,255,.25)',
        display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ position:'absolute', inset:3, borderRadius:5,
          border:'1px solid rgba(0,242,255,.3)', animation:'cornerSpin 8s linear infinite',
          borderTopColor:'rgba(0,242,255,.8)' }} />
        <span style={{ fontSize:'clamp(11px,1.4vw,16px)', position:'relative', zIndex:1 }}>✏️</span>
      </div>

      {/* Title */}
      <div style={{ minWidth:0, flexShrink:1, flex:1 }}>
        <div style={{ fontSize:'clamp(12px,1.5vw,18px)', fontWeight:900, fontFamily:"'Orbitron',monospace",
          color:'#ffffff', letterSpacing:3, textTransform:'uppercase',
          textShadow:'0 0 40px rgba(0,242,255,.6)', animation:'headerGlow 4s ease infinite',
          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {puzzle?.title || 'CROSSWORD'}
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <span style={{ fontSize:'clamp(8px,0.9vw,12px)', color:'rgba(0,242,255,.45)', fontFamily:"'Orbitron',monospace", letterSpacing:3 }}>{gameId}</span>
          <span style={{ color:'rgba(0,242,255,.1)', fontSize:'clamp(8px,0.9vw,12px)' }}>◆</span>
          <span style={{ fontSize:'clamp(8px,0.9vw,12px)', color:'rgba(0,242,255,.3)', fontFamily:"'Orbitron',monospace", letterSpacing:3, textTransform:'uppercase' }}>{status}</span>
        </div>
      </div>

      {/* Elapsed timer */}
      <div style={{ flexShrink:0,
        background:status==='active'?'linear-gradient(135deg,rgba(0,242,255,.12),rgba(0,242,255,.05))':'rgba(255,255,255,.02)',
        border:`1px solid ${status==='active'?'rgba(0,242,255,.35)':'rgba(255,255,255,.06)'}`,
        borderRadius:10, padding:'clamp(2px,.4vh,5px) clamp(10px,1.4vw,18px)',
        display:'flex', flexDirection:'column', alignItems:'center',
        boxShadow:status==='active'?'0 0 20px rgba(0,242,255,.2)':'none' }}>
        <span style={{ fontSize:'clamp(7px,0.8vw,10px)', fontWeight:700, letterSpacing:3,
          color:'rgba(0,242,255,.5)', fontFamily:"'Orbitron',monospace" }}>
          {status==='active'?'⏱ TIME':status==='completed'?'✓ DONE':'⏸ WAIT'}
        </span>
        <span style={{ fontSize:'clamp(16px,2.2vw,28px)', fontWeight:900, fontFamily:"'Orbitron',monospace",
          letterSpacing:2, lineHeight:1,
          color:status==='active'?'#00f2ff':'rgba(0,242,255,.3)',
          textShadow:status==='active'?'0 0 20px rgba(0,242,255,.9)':'none' }}>
          {formatTime(elapsed)}
        </span>
      </div>

      {/* Nav tabs */}
      <div style={{ display:'flex', gap:3, flexShrink:0 }}>
        {views.map(v => (
          <button key={v.id} onClick={() => onViewChange(v.id)} style={{
            padding:'clamp(8px,1vh,13px) clamp(10px,1.1vw,16px)', borderRadius:6,
            fontSize:'clamp(8px,0.9vw,12px)', fontWeight:700,
            fontFamily:"'Orbitron',monospace", letterSpacing:2, cursor:'pointer',
            color:view===v.id?'#00f2ff':'#1e3a5a',
            background:view===v.id?'rgba(0,242,255,.12)':'transparent',
            border:`1px solid ${view===v.id?'rgba(0,242,255,.32)':'transparent'}`,
            transition:'all .2s' }}>
            {v.label}
          </button>
        ))}
      </div>

      <button onClick={copyLink} style={{
        background:copied?'rgba(0,255,136,.15)':'rgba(0,242,255,.07)',
        border:`1px solid ${copied?'rgba(0,255,136,.4)':'rgba(0,242,255,.18)'}`,
        color:copied?'#00ff88':'rgba(0,242,255,.7)',
        padding:'clamp(8px,1vh,13px) clamp(10px,1.1vw,16px)', borderRadius:6, cursor:'pointer',
        fontSize:'clamp(8px,0.9vw,12px)', fontWeight:700, fontFamily:"'Orbitron',monospace",
        letterSpacing:2, transition:'all .2s', flexShrink:0 }}>
        {copied?'✓':'⎘ LINK'}
      </button>

      {status==='active' && (
        <div style={{ display:'flex', alignItems:'center', gap:5,
          background:'rgba(255,0,153,.09)', border:'1px solid rgba(255,0,153,.3)',
          borderRadius:16, padding:'clamp(4px,.6vh,7px) clamp(9px,1vw,13px)', flexShrink:0 }}>
          <div style={{ width:'clamp(5px,.7vw,8px)', height:'clamp(5px,.7vw,8px)', borderRadius:'50%', background:'#ff0099',
            animation:'liveFlash .9s ease infinite' }} />
          <span style={{ fontSize:'clamp(8px,0.9vw,12px)', fontWeight:700, color:'#ff0099',
            fontFamily:"'Orbitron',monospace", letterSpacing:3 }}>LIVE</span>
        </div>
      )}
    </div>
  );
}

// ─── Main Display View (3-column) ─────────────────────────────────────────────
function MainView({
  puzzle, filledCells, scores, teamScores, teams, gameId, anims, status,
  flashMsg, revealFlash, winSize, elapsed,
  timerDuration, startedAt, timerRevealed,
}) {
  const HEADER_H  = 56;
  const TIMER_H   = timerDuration && startedAt && status==='active' ? 38 : 0;
  const narrow    = winSize.w < 860;
  // Side panels scale with viewport width (capped) so on wide 4K/55" screens the
  // clue lists + scoreboard grow to soak up horizontal space and stay readable,
  // instead of leaving big empty margins around a centered grid.
  const PANEL_W   = narrow ? 0 : Math.round(Math.min(470, Math.max(195, winSize.w * 0.16)));
  const SCORE_W   = narrow ? 0 : Math.round(Math.min(440, Math.max(190, winSize.w * 0.145)));
  const gridAreaW = winSize.w - PANEL_W - SCORE_W;
  const gridAreaH = winSize.h - HEADER_H - TIMER_H;
  const cs        = computeCellSize(puzzle, gridAreaW, gridAreaH);
  const GAP       = Math.max(2, Math.round(cs*.05));
  const compact   = PANEL_W < 230;
  const across    = (puzzle?.clues||[]).filter(c=>c.direction==='across');
  const down      = (puzzle?.clues||[]).filter(c=>c.direction==='down');

  return (
    <div style={{ flex:1, display:'flex', overflow:'hidden', position:'relative', flexDirection:'column' }}>
      <div style={{ flex:1, display:'flex', overflow:'hidden', position:'relative' }}>

        {/* LEFT — Across clues */}
        {PANEL_W > 0 && (
          <div style={{ width:PANEL_W, flexShrink:0, overflow:'hidden',
            background:'rgba(2,5,18,.9)', backdropFilter:'blur(20px)',
            borderRight:'1px solid rgba(0,242,255,.1)',
            display:'flex', flexDirection:'column',
            animation:'slideInLeft .4s ease' }}>
            <ClueListPanel title="ACROSS" clues={across} filledCells={filledCells} compact={compact} />
          </div>
        )}

        {/* CENTER — Grid */}
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center',
          position:'relative', overflow:'hidden' }}>

          {/* Answer flash */}
          {flashMsg && (
            <div style={{ position:'absolute', top:16, left:'50%',
              background:`linear-gradient(135deg,${flashMsg.color||'#00f2ff'}22,${flashMsg.color||'#00f2ff'}08)`,
              border:`2px solid ${flashMsg.color||'#00f2ff'}66`,
              padding:'10px 32px', borderRadius:48, fontWeight:900, fontSize:16, zIndex:200,
              fontFamily:"'Orbitron',monospace", letterSpacing:3,
              animation:'flashAnim 2.6s ease forwards', whiteSpace:'nowrap',
              color:'#ffffff', textShadow:`0 0 24px ${flashMsg.color||'#00f2ff'}`,
              boxShadow:`0 12px 80px rgba(0,0,0,.95),0 0 80px ${flashMsg.color||'#00f2ff'}33`,
              backdropFilter:'blur(28px)' }}>{flashMsg.text}</div>
          )}

          {/* Reveal flash */}
          {revealFlash && (
            <div style={{ position:'absolute', top:flashMsg?72:16, left:'50%',
              background:'linear-gradient(135deg,rgba(255,153,0,.18),rgba(255,153,0,.06))',
              border:'2px solid rgba(255,153,0,.5)',
              padding:'10px 32px', borderRadius:48, fontWeight:900, fontSize:15, zIndex:201,
              fontFamily:"'Orbitron',monospace", letterSpacing:3,
              animation:'revealFlash 3.2s ease forwards', whiteSpace:'nowrap',
              color:'#ffaa00', textShadow:'0 0 24px rgba(255,153,0,.9)',
              backdropFilter:'blur(28px)' }}>👁 ANSWERS REVEALED BY ADMIN</div>
          )}

          {/* Grid */}
          {puzzle ? (
            <div style={{ perspective:'1600px', perspectiveOrigin:'50% 22%' }}>
              <div style={{ display:'inline-block', transform:'rotateX(2.5deg)', transformStyle:'preserve-3d',
                animation:'gridBreath 10s ease infinite' }}>
                {Array.from({ length:puzzle.height }).map((_,r) => (
                  <div key={r} style={{ display:'flex', gap:GAP, marginBottom:GAP }}>
                    {Array.from({ length:puzzle.width }).map((_,c) => (
                      <CrosswordCell key={`${r}_${c}`} row={r} col={c}
                        puzzle={puzzle} filledCells={filledCells} anims={anims} cs={cs} />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ width:'55vw', minHeight:'55vh', display:'flex', alignItems:'center', justifyContent:'center',
              background: status==='deleted' ? 'rgba(255,75,75,.04)' : 'rgba(0,242,255,.02)',
              border: `1px solid ${status==='deleted' ? 'rgba(255,75,75,.15)' : 'rgba(0,242,255,.07)'}`,
              borderRadius:24, color: status==='deleted' ? 'rgba(255,75,75,.5)' : 'rgba(0,242,255,.18)', fontSize:14,
              fontFamily:"'Orbitron',monospace", letterSpacing:6, flexDirection:'column', gap:16,
              padding:'clamp(24px,3vh,48px)' }}>
              {(status==='loading' || (status==='active' && !puzzle)) && 'LOADING…'}
              {status==='deleted' && <>
                <span style={{ fontSize:32 }}>🗑️</span>
                <span>GAME REMOVED</span>
              </>}
              {status !== 'loading' && status !== 'deleted' && !(status==='active' && !puzzle) && <>
                <span>AWAITING START</span>
                {teams.length > 0 && (
                  <div style={{ marginTop:8, display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
                    <div style={{ fontSize:10, color:'rgba(0,242,255,.35)', letterSpacing:4, marginBottom:4 }}>
                      TEXT /join {gameId} [TEAM] TO JOIN
                    </div>
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap', justifyContent:'center' }}>
                      {teams.map((t, i) => (
                        <div key={t.id} style={{
                          display:'flex', alignItems:'center', gap:6,
                          background:`${t.color}18`, border:`1px solid ${t.color}55`,
                          borderRadius:8, padding:'6px 14px', fontFamily:"'Orbitron',monospace",
                          fontSize:11, letterSpacing:3, color:t.color,
                        }}>
                          <div style={{ width:8, height:8, borderRadius:'50%', background:t.color, flexShrink:0 }} />
                          {(i + 1)}. {t.name.toUpperCase()}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>}
            </div>
          )}

          {/* WhatsApp play prompt */}
          {status==='active' && (
            <div style={{ position:'absolute', bottom: timerDuration ? 46 : 16,
              left:'50%', transform:'translateX(-50%)',
              padding:'7px 20px', background:'rgba(37,211,102,.06)', border:'1px solid rgba(37,211,102,.18)',
              borderRadius:26, display:'flex', alignItems:'center', gap:10,
              backdropFilter:'blur(18px)', pointerEvents:'none', whiteSpace:'nowrap' }}>
              <span style={{ fontSize:15 }}>💬</span>
              <span style={{ fontSize:11, color:'rgba(37,211,102,.7)', fontFamily:"'Orbitron',monospace", letterSpacing:2 }}>
                TEXT <span style={{ color:'#25D366', background:'rgba(37,211,102,.15)', padding:'2px 8px', borderRadius:5 }}>3A JUNGLE</span> TO PLAY
              </span>
            </div>
          )}

          {/* Countdown timer bar — positioned at bottom of grid area */}
          <CountdownBar timerDuration={timerDuration} startedAt={startedAt} status={status} revealed={timerRevealed} />

          {/* Mobile clue strip when no side panels */}
          {narrow && puzzle && (
            <div style={{ position:'absolute', bottom:TIMER_H, left:0, right:0,
              background:'rgba(2,5,18,.97)', borderTop:'1px solid rgba(0,242,255,.15)',
              maxHeight:'32%', display:'flex', overflow:'hidden' }}>
              <div style={{ flex:1, borderRight:'1px solid rgba(0,242,255,.08)', overflow:'auto' }}>
                <ClueListPanel title="ACROSS" clues={across} filledCells={filledCells} compact />
              </div>
              <div style={{ flex:1, overflow:'auto' }}>
                <ClueListPanel title="DOWN" clues={down} filledCells={filledCells} compact />
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — Elapsed timer + progress + scores + Down clues */}
        {SCORE_W > 0 && (
          <div style={{ width:SCORE_W, flexShrink:0, overflow:'hidden',
            background:'rgba(2,5,18,.9)', backdropFilter:'blur(20px)',
            borderLeft:'1px solid rgba(0,242,255,.1)',
            display:'flex', flexDirection:'column',
            animation:'slideInRight .4s ease' }}>

            <div style={{ padding:compact?'10px 14px 6px':'12px 18px 8px', flexShrink:0 }}>
              <TimerWidget elapsed={elapsed} status={status} compact={compact} />
            </div>

            <div style={{ padding:'0 14px 8px', flexShrink:0 }}>
              <ProgressBar puzzle={puzzle} filledCells={filledCells} />
            </div>

            <div style={{ height:1, background:'rgba(0,242,255,.08)', flexShrink:0 }} />
            {teamScores.length > 0 && (
              <div style={{ flexShrink:0 }}>
                <MiniTeamScores teamScores={teamScores} />
                <div style={{ height:1, background:'rgba(0,242,255,.08)' }} />
              </div>
            )}
            <div style={{ flexShrink:0 }}>
              <MiniLeaderboard scores={scores} compact={compact} />
            </div>
            <div style={{ height:1, background:'rgba(0,242,255,.08)', flexShrink:0 }} />

            <div style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column' }}>
              <ClueListPanel title="DOWN" clues={down} filledCells={filledCells} compact={compact} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Scores View ──────────────────────────────────────────────────────────────
function ScoresView({ scores, elapsed, status }) {
  const top    = [...(scores||[])].sort((a,b)=>b.score-a.score);
  const metals = ['#ffd700','#d0d0d0','#cd7f32'];
  return (
    <div style={{ flex:1, display:'flex', gap:28, padding:'24px 36px', overflow:'hidden' }}>
      <div style={{ flex:1, overflow:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:22 }}>
          <div style={{ fontSize:'clamp(13px,1.4vw,26px)', fontWeight:700, letterSpacing:6, color:'#ffd700',
            fontFamily:"'Orbitron',monospace", textShadow:'0 0 32px rgba(255,215,0,.7)',
            display:'flex', alignItems:'center', gap:10 }}>
            <span>🏆</span> Leaderboard
          </div>
          <div style={{ background:'rgba(0,242,255,.08)', border:'1px solid rgba(0,242,255,.25)',
            borderRadius:10, padding:'5px 16px', textAlign:'center' }}>
            <div style={{ fontSize:'clamp(8px,.8vw,14px)', color:'rgba(0,242,255,.5)', fontFamily:"'Orbitron',monospace",
              letterSpacing:3, marginBottom:2 }}>{status==='active'?'⏱ ELAPSED':'STATUS'}</div>
            <div style={{ fontSize:'clamp(22px,2.4vw,44px)', fontWeight:900, fontFamily:"'Orbitron',monospace",
              color:'#00f2ff', textShadow:'0 0 18px rgba(0,242,255,.8)' }}>{formatTime(elapsed)}</div>
          </div>
        </div>
        {top.map((s,i) => (
          <div key={s.jid||i} style={{ display:'flex', alignItems:'center', gap:14,
            padding:'12px 18px', marginBottom:8,
            background:i===0?'linear-gradient(135deg,rgba(255,215,0,.12),rgba(255,215,0,.03))':'rgba(255,255,255,.025)',
            border:`1px solid ${i<3?metals[i]+'44':'rgba(255,255,255,.05)'}`, borderRadius:12,
            animation:`slideInRight .4s ease ${i*.07}s both`, opacity:s.left?.4:1 }}>
            <div style={{ width:'clamp(42px,3.6vw,64px)', height:'clamp(42px,3.6vw,64px)', borderRadius:10, flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center',
              background:i<3?`${metals[i]}1e`:'rgba(255,255,255,.03)',
              border:`1px solid ${i<3?metals[i]+'44':'rgba(255,255,255,.05)'}`,
              fontSize:'clamp(18px,1.8vw,32px)', fontWeight:900, color:i<3?metals[i]:'#2a3a54',
              fontFamily:"'Orbitron',monospace" }}>{i+1}</div>
            <div style={{ width:4, height:'clamp(42px,3.6vw,64px)', borderRadius:3, flexShrink:0,
              background:s.teamColor||(i<3?metals[i]:'#1a2d4a') }} />
            <div style={{ flex:1, minWidth:0,
              fontSize:i===0?'clamp(26px,2.6vw,48px)':i===1?'clamp(20px,2vw,38px)':'clamp(17px,1.8vw,32px)', fontWeight:800, fontFamily:"'Exo 2',sans-serif",
              color:i===0?'#ffd700':i===1?'#d0d0d0':i===2?'#cd7f32':'#b0ccee',
              textShadow:i===0?'0 0 28px rgba(255,215,0,.85)':'none',
              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{s.name}</div>
            <div style={{ fontSize:i===0?'clamp(42px,4vw,72px)':i<3?'clamp(30px,3vw,52px)':'clamp(22px,2.2vw,40px)', fontWeight:900, fontFamily:"'Orbitron',monospace",
              color:i===0?'#ffd700':(s.teamColor||'#00f2ff'),
              textShadow:i===0?'0 0 28px rgba(255,215,0,.9)':`0 0 14px ${s.teamColor||'#00f2ff'}66` }}>
              <AnimatedScore value={s.score} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Join View ────────────────────────────────────────────────────────────────
function JoinView({ gameId, puzzle, scores }) {
  const displayUrl = `${window.location.origin}/?game=${gameId}&view=display`;
  const [copied, setCopied] = useState(null);
  const copy = (text, id) => { navigator.clipboard.writeText(text); setCopied(id); setTimeout(()=>setCopied(null),2000); };
  return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center',
      gap:48, padding:'28px 40px', flexWrap:'wrap', overflow:'auto' }}>
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
        <div style={{ fontSize:9, letterSpacing:6, color:'rgba(0,242,255,.6)',
          fontFamily:"'Orbitron',monospace", fontWeight:700 }}>OPEN ON YOUR SCREEN</div>
        <QRCode url={displayUrl} size={190} />
        <div style={{ fontSize:9, color:'rgba(0,242,255,.3)', fontFamily:'monospace',
          letterSpacing:1, maxWidth:230, textAlign:'center', wordBreak:'break-all' }}>{displayUrl}</div>
        <button onClick={()=>copy(displayUrl,'url')} style={{
          background:copied==='url'?'rgba(0,255,136,.14)':'rgba(0,242,255,.07)',
          border:`1px solid ${copied==='url'?'rgba(0,255,136,.4)':'rgba(0,242,255,.2)'}`,
          color:copied==='url'?'#00ff88':'#00f2ff',
          padding:'7px 18px', borderRadius:8, cursor:'pointer',
          fontSize:9, fontWeight:700, fontFamily:"'Orbitron',monospace", letterSpacing:2 }}>
          {copied==='url'?'✓ COPIED!':'⎘ COPY LINK'}
        </button>
      </div>
      <div style={{ width:1, height:340, background:'rgba(0,242,255,.08)', flexShrink:0 }} />
      <div style={{ maxWidth:380 }}>
        <div style={{ fontSize:28, fontWeight:900, fontFamily:"'Orbitron',monospace", color:'#ffffff',
          marginBottom:4, letterSpacing:2, textShadow:'0 0 36px rgba(0,242,255,.5)' }}>HOW TO PLAY</div>
        <div style={{ fontSize:11, color:'#25D366', fontFamily:"'Orbitron',monospace",
          letterSpacing:4, marginBottom:22, fontWeight:700 }}>VIA WHATSAPP</div>
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {[
            { n:'1', t:"Text the WhatsApp number shown on screen", ex:null },
            { n:'2', t:"Send clue number + direction + answer", ex:'3A JUNGLE' },
            { n:'3', t:"Or just the answer if it's unique", ex:'JUNGLE' },
            { n:'4', t:"Watch your answer appear live on the board!", ex:null },
          ].map(({n,t,ex}) => (
            <div key={n} style={{ display:'flex', gap:12, alignItems:'flex-start',
              animation:`slideInRight .4s ease ${+n*.1}s both` }}>
              <div style={{ width:34, height:34, borderRadius:8, flexShrink:0,
                background:'rgba(0,242,255,.1)', border:'1px solid rgba(0,242,255,.28)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:15, fontWeight:900, color:'#00f2ff', fontFamily:"'Orbitron',monospace" }}>{n}</div>
              <div>
                <div style={{ fontSize:15, color:'#b0ccee', fontFamily:"'Share Tech Mono',monospace", lineHeight:1.5 }}>{t}</div>
                {ex && <div style={{ display:'inline-block', marginTop:5,
                  background:'rgba(37,211,102,.12)', border:'1px solid rgba(37,211,102,.3)',
                  color:'#25D366', padding:'3px 11px', borderRadius:6, fontSize:15, fontWeight:700,
                  fontFamily:"'Orbitron',monospace", letterSpacing:3 }}>{ex}</div>}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop:26, padding:'14px 22px',
          background:'rgba(255,215,0,.07)', border:'1px solid rgba(255,215,0,.25)', borderRadius:12,
          display:'flex', alignItems:'center', gap:16 }}>
          <div>
            <div style={{ fontSize:8, color:'rgba(255,215,0,.6)', fontFamily:"'Orbitron',monospace",
              letterSpacing:5, marginBottom:3, fontWeight:700 }}>GAME CODE</div>
            <div style={{ fontSize:32, fontWeight:900, color:'#ffd700', fontFamily:"'Orbitron',monospace",
              letterSpacing:6, textShadow:'0 0 36px rgba(255,215,0,.85)' }}>{gameId}</div>
          </div>
          <div style={{ flex:1 }} />
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:11, color:'rgba(255,215,0,.6)', fontFamily:"'Orbitron',monospace", marginBottom:3 }}>{puzzle?.title||'CROSSWORD'}</div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,.2)', fontFamily:"'Orbitron',monospace" }}>{scores.length} player{scores.length!==1?'s':''}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Audio Unlock Button ──────────────────────────────────────────────────────
function AudioUnlockBtn({ onUnlock }) {
  return (
    <button
      onClick={onUnlock}
      style={{
        position:'fixed', bottom:18, right:18, zIndex:9999,
        background:'rgba(0,242,255,.14)', border:'2px solid rgba(0,242,255,.5)',
        borderRadius:40, padding:'9px 20px', cursor:'pointer',
        display:'flex', alignItems:'center', gap:8,
        backdropFilter:'blur(16px)',
        animation:'liveFlash 2s ease infinite',
        boxShadow:'0 0 24px rgba(0,242,255,.3)',
      }}
    >
      <span style={{ fontSize:16 }}>🔇</span>
      <span style={{ fontSize:10, fontWeight:700, color:'#00f2ff',
        fontFamily:"'Orbitron',monospace", letterSpacing:2 }}>
        TAP TO ENABLE SOUND
      </span>
    </button>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function CrosswordBoard({ tvMode = false, onGameCompleted, gameId: propGameId } = {}) {
  const params  = new URLSearchParams(window.location.search);
  const gameId  = propGameId || params.get('game') || 'DEMO';
  const apiBase = process.env.REACT_APP_API_URL || '/api';
  const winSize = useWindowSize();
  const sounds  = useSound();
  const bgStarted = useRef(false);
  const [audioUnlocked, setAudioUnlocked] = useState(LITE);

  // Auto-mark audio as unlocked on first user gesture
  useEffect(() => {
    const mark = () => setAudioUnlocked(true);
    document.addEventListener('click', mark, { once: true });
    document.addEventListener('keydown', mark, { once: true });
    document.addEventListener('touchstart', mark, { once: true });
    return () => {
      document.removeEventListener('click', mark);
      document.removeEventListener('keydown', mark);
      document.removeEventListener('touchstart', mark);
    };
  }, []);

  const [view, setView] = useState(tvMode ? 'display' : (params.get('view') || 'display'));
  const handleViewChange = useCallback((v) => {
    if (tvMode) return; // TV mode locks to display view
    setView(v);
    const u = new URL(window.location.href);
    u.searchParams.set('view', v);
    window.history.replaceState(null, '', u.toString());
  }, [tvMode]);

  const [puzzle,        setPuzzle]        = useState(null);
  const [filledCells,   setFilledCells]   = useState({});
  const [scores,        setScores]        = useState([]);
  const [teams,         setTeams]         = useState([]);
  const [teamScores,    setTeamScores]    = useState([]);
  const [status,        setStatus]        = useState('loading');
  const [startedAt,     setStartedAt]     = useState(null);
  const [timerDuration, setTimerDuration] = useState(null); // seconds, if set
  const [flashMsg,        setFlashMsg]        = useState(null);
  const [revealFlash,     setRevealFlash]     = useState(false);
  const [timerRevealed,   setTimerRevealed]   = useState(false); // admin revealed mid-game
  const [anims,         triggerAnim]      = useCellAnimations();
  const [showWinner,    setShowWinner]    = useState(false);
  const [fireworks,     setFireworks]     = useState(false);

  const elapsed = useElapsedTimer(startedAt, status);

  // Start background music once on first active game
  useEffect(() => {
    if (status === 'active' && !bgStarted.current) {
      bgStarted.current = true;
      sounds.startMusic();
    }
  }, [status]); // eslint-disable-line

  // Initial REST load
  useEffect(() => {
    if (!gameId || gameId==='DEMO') { setStatus('waiting'); return; }
    fetch(`${apiBase}/games/${gameId}`)
      .then(r => r.json())
      .then(async game => {
        setStatus(game.status || 'waiting');
        if (game.startedAt)     setStartedAt(game.startedAt);
        if (game.timerDuration) setTimerDuration(game.timerDuration);
        const enriched = {};
        Object.entries(game.cells || {}).forEach(([k, cell]) => {
          const parts = k.split('_');
          enriched[k] = { ...cell, startRow:parseInt(parts[0]), startCol:parseInt(parts[1]), direction:parts[2] };
        });
        setFilledCells(enriched);
        setScores(Object.values(game.scores || {}).sort((a,b) => b.score-a.score));
        if (game.teams?.length > 0) {
          setTeams(game.teams);
          const tm = {};
          game.teams.forEach(t => { tm[t.id] = { ...t, score: 0, playerCount: 0 }; });
          Object.values(game.scores || {}).forEach(s => {
            if (s.teamId && tm[s.teamId]) { tm[s.teamId].score += (s.score || 0); tm[s.teamId].playerCount++; }
          });
          setTeamScores(Object.values(tm).sort((a,b) => b.score - a.score));
        }
        if (game.puzzleId) {
          const puz = await fetch(`${apiBase}/puzzles/${game.puzzleId}`).then(r => r.json());
          setPuzzle(enrichPuzzle(puz));
        }
        if (game.status === 'completed') {
          setShowWinner(true);
          setFireworks(true);
          setTimeout(() => setFireworks(false), 8000);
        }
      })
      .catch(() => setStatus('error'));
  }, [gameId, apiBase]);

  // Ably: cell filled
  useAblyChannel(`crossword:game:${gameId}:display`, 'cell-filled', (msg) => {
    const d = msg.data;
    const cellKey = `${d.row}_${d.col}_${d.direction}`;
    setFilledCells(prev => ({ ...prev, [cellKey]: {
      answer:d.answer, direction:d.direction, startRow:d.row, startCol:d.col,
      teamColor:d.team.color, initials:d.player.initials,
    }}));
    triggerAnim(`${d.row}_${d.col}`, { initials:d.player.initials, teamColor:d.team.color });
    if (!d.revealedByAdmin) {
      // Play correct sound for every genuine player answer
      sounds.correct();
      const dir = d.direction==='across'?'A':'D';
      setFlashMsg({ text:`${d.player.name}  ·  ${d.clueNumber}${dir}  ·  ${d.answer}`, color:d.team.color });
      setTimeout(() => setFlashMsg(null), 2600);
    }
  });

  // Ably: score update
  useAblyChannel(`crossword:game:${gameId}:display`, 'score-update', (msg) => {
    setScores(msg.data?.scores || []);
    if (msg.data?.teamScores?.length > 0) setTeamScores(msg.data.teamScores);
  });

  // Ably: game-state change
  useAblyChannel(`crossword:game:${gameId}:display`, 'game-state', (msg) => {
    const d = msg.data;
    setStatus(d?.state);
    if (d?.state === 'active') {
      if (d?.startedAt)     setStartedAt(d.startedAt);
      if (d?.timerDuration) setTimerDuration(d.timerDuration);
      setTimerRevealed(false); // reset revealed flag on fresh start
    }
    if (d?.state === 'completed') {
      sounds.gameOver();
      setTimerDuration(null);
      setShowWinner(true);
      setFireworks(true);
      setTimeout(() => setFireworks(false), 8000);
      if (onGameCompleted) setTimeout(onGameCompleted, 9000);
    }
  });

  // Ably: admin reveal-all — freeze countdown bar, play sound, notify
  useAblyChannel(`crossword:game:${gameId}:display`, 'reveal-all', () => {
    sounds.adminReveal();
    setRevealFlash(true);
    setTimerRevealed(true);   // freeze the countdown bar showing REVEALED
    // NOTE: do NOT null startedAt — elapsed timer should keep showing total time
    setTimeout(() => setRevealFlash(false), 3500);
  });

  // Ably: game deleted — gracefully exit
  useAblyChannel('crossword:admin', 'game-deleted', (msg) => {
    if (msg.data?.gameId !== gameId) return;
    setStatus('deleted');
    if (onGameCompleted) setTimeout(onGameCompleted, 3000);
  });

  return (
    <div style={{ position:'fixed', inset:0, background:'#02040c',
      fontFamily:"'Share Tech Mono',monospace", color:'#e8f4ff',
      display:'flex', flexDirection:'column', overflow:'hidden',
      /* safe-area insets for notched phones/TVs */
      paddingTop:'env(safe-area-inset-top)',
      paddingBottom:'env(safe-area-inset-bottom)',
      paddingLeft:'env(safe-area-inset-left)',
      paddingRight:'env(safe-area-inset-right)',
    }}>
      <style>{STYLES}</style>

      {/* Ambient background */}
      <div style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none', overflow:'hidden' }}>
        <div style={{ position:'absolute', width:'60vw', height:'60vw', top:'-22%', left:'-14%',
          background:'radial-gradient(circle,rgba(0,242,255,.09) 0%,transparent 65%)',
          animation:'ambientOrb1 7s ease infinite' }} />
        <div style={{ position:'absolute', width:'50vw', height:'50vw', bottom:'-14%', right:'-8%',
          background:'radial-gradient(circle,rgba(255,0,153,.07) 0%,transparent 65%)',
          animation:'ambientOrb2 9.5s ease infinite 2s' }} />
        <div style={{ position:'absolute', width:'38vw', height:'38vw', top:'32%', left:'40%',
          background:'radial-gradient(circle,rgba(0,255,136,.05) 0%,transparent 65%)',
          animation:'ambientOrb3 13s ease infinite 5s' }} />
        <div style={{ position:'absolute', width:'100%', height:2,
          background:'linear-gradient(90deg,transparent,rgba(0,242,255,.22) 30%,rgba(0,242,255,.44) 50%,rgba(0,242,255,.22) 70%,transparent)',
          animation:'scanMove 15s linear infinite' }} />
        <div style={{ position:'absolute', inset:0,
          backgroundImage:'linear-gradient(rgba(0,242,255,.015) 1px,transparent 1px),linear-gradient(90deg,rgba(0,242,255,.015) 1px,transparent 1px)',
          backgroundSize:'80px 80px' }} />
      </div>

      {!tvMode && (
        <NavBar gameId={gameId} view={view} onViewChange={handleViewChange}
          puzzle={puzzle} status={status} elapsed={elapsed} />
      )}
      {tvMode && (
        <div style={{ position:'relative', zIndex:100, height:'clamp(46px,5vh,60px)', padding:'0 clamp(16px,2vw,28px)',
          display:'flex', alignItems:'center', gap:14,
          background:'linear-gradient(180deg,rgba(2,4,16,.99),rgba(2,5,14,.97))',
          borderBottom:'1px solid rgba(0,242,255,.1)', flexShrink:0 }}>
          <div style={{ fontSize:'clamp(14px,1.6vw,22px)', fontWeight:900, fontFamily:"'Orbitron',monospace",
            color:'#ffffff', letterSpacing:3, textShadow:'0 0 40px rgba(0,242,255,.6)',
            animation:'headerGlow 4s ease infinite', flex:1,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {puzzle?.title || 'CROSSWORD'}
          </div>
          {status === 'active' && (
            <div style={{ display:'flex', alignItems:'center', gap:6,
              background:'rgba(255,0,153,.09)', border:'1px solid rgba(255,0,153,.3)',
              borderRadius:16, padding:'4px 14px' }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:'#ff0099',
                animation:'liveFlash .9s ease infinite' }} />
              <span style={{ fontSize:'clamp(10px,1vw,14px)', fontWeight:700, color:'#ff0099',
                fontFamily:"'Orbitron',monospace", letterSpacing:3 }}>LIVE</span>
            </div>
          )}
        </div>
      )}

      <div style={{ flex:1, display:'flex', flexDirection:'column', position:'relative', zIndex:5, overflow:'hidden' }}>
        {view==='display' && (
          <MainView
            puzzle={puzzle} filledCells={filledCells} scores={scores}
            teamScores={teamScores} teams={teams} gameId={gameId}
            anims={anims} status={status} flashMsg={flashMsg}
            revealFlash={revealFlash} winSize={winSize} elapsed={elapsed}
            timerDuration={timerDuration} startedAt={startedAt}
            timerRevealed={timerRevealed}
          />
        )}
        {view==='scores' && <ScoresView scores={scores} elapsed={elapsed} status={status} />}
        {view==='join'   && <JoinView gameId={gameId} puzzle={puzzle} scores={scores} />}
      </div>

      <FireworksCanvas active={fireworks} />
      <GameOverOverlay
        scores={scores} teamScores={teamScores} active={showWinner}
        onDismiss={() => setShowWinner(false)}
      />
      {!audioUnlocked && (
        <AudioUnlockBtn onUnlock={() => {
          sounds.forceUnlock();
          setAudioUnlocked(true);
        }} />
      )}
    </div>
  );
}
